import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    SearchIcon,
    ChevronLeft,
    ChevronRight,
    Zap,
    ShieldCheck,
    Mail,
    UserCog,
    Loader2,
    X,
    ShieldAlert,
    MapPin,
    Activity,
    History,
    Globe,
    ArrowUpRight,
    Info,
    Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL as API_BASE } from '../../config/apiConfig';
import { toast } from 'react-toastify';
import { getPlanDetails } from '../../utils/planUtils';
import { cleanAddress, getAddressParts } from '../../utils/addressUtils';
import Tooltip from '../UI/Tooltip';

const InlineActionOverlay = ({ user, targetRole, onConfirm, onCancel, isUpdating }) => {
    const isPromoting = targetRole === 'admin';

    return (
        <motion.div
            initial={{ opacity: 0, x: 40, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.95 }}
            className="absolute left-[-280px] right-0 inset-y-[-4px] bg-white/98 backdrop-blur-xl z-50 flex items-center justify-between px-6 border-l-4 border-indigo-600 rounded-2xl shadow-2xl ring-1 ring-slate-200/50"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center gap-4 min-w-0 flex-1">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isPromoting ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-900'}`}>
                    <ShieldAlert className="w-5 h-5" />
                </div>
                <div className="flex flex-col min-w-0">
                    <p className="text-[11px] font-black text-slate-900 leading-none uppercase tracking-wider truncate">
                        Confirm {isPromoting ? 'Admin Promotion' : 'Role Demotion'}
                    </p>
                    <p className="text-[9px] font-bold text-slate-500 truncate mt-1">
                        Update <span className="text-indigo-600">{user.full_name || user.email}</span> to {targetRole}?
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                <button
                    onClick={onCancel}
                    disabled={isUpdating}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-all whitespace-nowrap"
                >
                    <X className="w-3.5 h-3.5" />
                    <span>Cancel</span>
                </button>
                <button
                    onClick={onConfirm}
                    disabled={isUpdating}
                    className={`h-11 px-6 rounded-xl flex items-center gap-2 text-[10px] font-black uppercase tracking-widest shadow-lg transition-all active:scale-95 whitespace-nowrap ${isPromoting
                        ? 'bg-indigo-600 text-white shadow-indigo-200 hover:bg-indigo-700'
                        : 'bg-slate-900 text-white shadow-slate-200 hover:bg-black'
                        }`}
                >
                    {isUpdating ? (
                        <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Updating...</span>
                        </>
                    ) : (
                        <>
                            <ShieldCheck className="w-3.5 h-3.5" />
                        </>
                    )}
                </button>
            </div>
        </motion.div>
    );
};

const InlineDeleteUserOverlay = ({ user, onConfirm, onCancel, isDeleting }) => {
    return (
        <motion.div
            initial={{ opacity: 0, x: 40, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.95 }}
            className="absolute left-[-280px] right-0 inset-y-[-4px] bg-red-50/98 backdrop-blur-xl z-50 flex items-center justify-between px-6 border-l-4 border-red-600 rounded-2xl shadow-2xl ring-1 ring-red-200/50"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center gap-4 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-red-100 text-red-600">
                    <ShieldAlert className="w-5 h-5 animate-pulse" />
                </div>
                <div className="flex flex-col min-w-0">
                    <p className="text-[11px] font-black text-red-900 leading-none uppercase tracking-wider truncate">
                        Confirm Delete User
                    </p>
                    <p className="text-[9px] font-bold text-red-600/80 truncate mt-1">
                        All user data and properties will be removed permanently!
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                <button
                    onClick={onCancel}
                    disabled={isDeleting}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 hover:bg-white/50 transition-all whitespace-nowrap"
                >
                    <X className="w-3.5 h-3.5" />
                    <span>Cancel</span>
                </button>
                <button
                    onClick={onConfirm}
                    disabled={isDeleting}
                    className="h-11 px-6 rounded-xl flex items-center gap-2 text-[10px] font-black uppercase tracking-widest shadow-lg transition-all active:scale-95 bg-red-600 text-white shadow-red-200 hover:bg-red-700 whitespace-nowrap"
                >
                    {isDeleting ? (
                        <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Deleting...</span>
                        </>
                    ) : (
                        <>
                            <Trash2 className="w-3.5 h-3.5" />
                        </>
                    )}
                </button>
            </div>
        </motion.div>
    );
};

const UserActivityPanel = ({ user, isOpen, onClose, onAnalysisDeleted }) => {
    const [activity, setActivity] = useState({ analyses: [], logs: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('properties');
    const [deletingAnalysisId, setDeletingAnalysisId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setDeletingAnalysisId(null);
            setIsDeleting(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && user) {
            const fetchActivity = async () => {
                setIsLoading(true);
                try {
                    const token = localStorage.getItem('accessToken');
                    const response = await fetch(`${API_BASE}/admin/users/${user.id}/activity`, {
                        headers: { 'Authorization': `Bearer ` + token }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setActivity(data);
                    }
                } catch (error) {
                    console.error("Error fetching activity:", error);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchActivity();
        }
    }, [isOpen, user]);

    const handleDeleteAnalysis = async (analysisId) => {
        setIsDeleting(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE}/admin/analyses/${analysisId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ` + token }
            });
            if (response.ok) {
                toast.success("Property analysis deleted successfully", {
                    icon: <ShieldCheck className="w-5 h-5 text-indigo-600" />,
                    className: 'rounded-2xl font-bold text-xs uppercase tracking-wider shadow-2xl border-l-4 border-indigo-600'
                });
                setActivity(prev => ({
                    ...prev,
                    analyses: prev.analyses.filter(a => a.id !== analysisId)
                }));
                if (onAnalysisDeleted) {
                    onAnalysisDeleted();
                }
            } else {
                const err = await response.json();
                toast.error(err.detail || "Failed to delete analysis");
            }
        } catch (error) {
            console.error("Failed to delete analysis:", error);
            toast.error("Network error occurred.");
        } finally {
            setIsDeleting(false);
            setDeletingAnalysisId(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-[100] cursor-pointer"
                    />
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 250 }}
                        className="fixed top-0 right-0 h-full w-full max-w-lg bg-white z-[101] border-l border-slate-100 flex flex-col shadow-2xl shadow-slate-900/10"
                    >
                        <div className="p-8 pb-6 bg-gradient-to-br from-indigo-50/50 to-white border-b border-indigo-100/50 relative">
                            <button
                                onClick={onClose}
                                className="absolute top-8 right-8 p-2 rounded-lg bg-white/80 hover:bg-white text-indigo-400 hover:text-indigo-600 transition-all active:scale-95 shadow-sm border border-indigo-50"
                            >
                                <X className="w-4 h-4" />
                            </button>

                            <div className="flex items-center gap-5 mb-8">
                                <div className="relative">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center text-white shadow-xl shadow-indigo-200 overflow-hidden">
                                        {user.profile_pic ? (
                                            <img src={user.profile_pic} alt={user.full_name || 'User'} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                        ) : (
                                            <span className="text-xl font-black">{(user.full_name || user.email).charAt(0).toUpperCase()}</span>
                                        )}
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-lg border-4 border-white shadow-sm" />
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <h2 className="text-xl font-black text-slate-900 tracking-tight truncate">{user.full_name || 'Verified Identity'}</h2>
                                        <ShieldCheck className="w-3.5 h-3.5 text-indigo-500" />
                                    </div>
                                    <p className="text-[10px] font-bold text-indigo-400/60 uppercase tracking-widest truncate mb-3">{user.email}</p>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border ${getPlanDetails(user.plan_name, user.subscription_status).colors}`}>
                                            {getPlanDetails(user.plan_name, user.subscription_status).label}
                                        </span>
                                        <span className="px-2 py-0.5 rounded-md bg-indigo-600 text-white text-[8px] font-black uppercase tracking-widest shadow-md shadow-indigo-100">
                                            {user.access_role || 'Member'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-8">
                                {[
                                    { id: 'properties', label: 'History', icon: MapPin },
                                    { id: 'logs', label: 'Security', icon: ShieldCheck }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`pb-2 text-[10px] font-black uppercase tracking-[0.2em] transition-all relative ${activeTab === tab.id ? 'text-indigo-600' : 'text-slate-400 hover:text-indigo-400'}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <tab.icon className={`w-3 h-3 ${activeTab === tab.id ? 'text-indigo-600' : ''}`} />
                                            <span>{tab.label}</span>
                                        </div>
                                        {activeTab === tab.id && (
                                            <motion.div layoutId="compactTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-8 pt-6 no-scrollbar bg-slate-50/30">
                            {isLoading ? (
                                <div className="h-full flex flex-col items-center justify-center gap-4 opacity-40">
                                    <div className="w-8 h-8 border-2 border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
                                    <p className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.3em]">Refreshing...</p>
                                </div>
                            ) : activeTab === 'properties' ? (
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-[9px] font-black text-indigo-300 uppercase tracking-[0.3em]">Activity Log</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[8px] font-black text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md border border-indigo-100">
                                                {activity.analyses.filter(a => a.is_original).length} Original
                                            </span>
                                            <span className="text-[8px] font-black text-amber-600 bg-amber-50 px-2 py-1 rounded-md border border-amber-100">
                                                {activity.analyses.filter(a => !a.is_original).length} Linked
                                            </span>
                                        </div>
                                    </div>

                                    {activity.analyses.length > 0 ? (
                                        <div className="relative pl-8 space-y-3">
                                            <div className="absolute left-0 top-0 bottom-4 w-px bg-indigo-100" />
                                            {activity.analyses.map((analysis, idx) => (
                                                <motion.div
                                                    key={analysis.id}
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: idx * 0.03 }}
                                                    className="relative group"
                                                >
                                                    <div className="absolute -left-8 top-[26px] w-8 h-px bg-indigo-50 group-hover:bg-indigo-200 transition-colors" />
                                                    <div className="absolute -left-[3px] top-[24px] w-1.5 h-1.5 rounded-full bg-indigo-200 group-hover:bg-indigo-600 transition-all z-10 scale-75 group-hover:scale-100" />
                                                    <div className="p-4 bg-white border border-indigo-50 rounded-2xl hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-500/5 transition-all cursor-pointer relative overflow-hidden">
                                                        <AnimatePresence>
                                                            {deletingAnalysisId === analysis.id && (
                                                                <motion.div
                                                                    initial={{ opacity: 0 }}
                                                                    animate={{ opacity: 1 }}
                                                                    exit={{ opacity: 0 }}
                                                                    className="absolute inset-0 bg-rose-50/95 backdrop-blur-xs flex items-center justify-between px-4 z-20"
                                                                >
                                                                    <div className="flex flex-col min-w-0 pr-2">
                                                                        <span className="text-[10px] font-black text-rose-600 uppercase tracking-wider truncate">Confirm Delete</span>
                                                                        <span className="text-[8px] font-bold text-rose-500/80 truncate">Remove property & linked records?</span>
                                                                    </div>
                                                                    <div className="flex gap-2 flex-shrink-0">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setDeletingAnalysisId(null);
                                                                            }}
                                                                            className="px-3 py-1.5 bg-white text-slate-500 hover:text-slate-900 border border-slate-200 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all"
                                                                        >
                                                                            No
                                                                        </button>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDeleteAnalysis(analysis.id);
                                                                            }}
                                                                            disabled={isDeleting}
                                                                            className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-[9px] font-black uppercase tracking-wider transition-all flex items-center gap-1"
                                                                        >
                                                                            {isDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Yes"}
                                                                        </button>
                                                                    </div>
                                                                </motion.div>
                                                            )}
                                                        </AnimatePresence>

                                                        <div className="flex items-center justify-between gap-4">
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center gap-2 mb-1.5">
                                                                    <span className="text-[8px] font-black text-indigo-300 uppercase tracking-widest">{formatDate(analysis.timestamp)}</span>
                                                                    <div className="w-1 h-1 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
                                                                    {!analysis.is_original && (
                                                                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-50 border border-amber-100/50">
                                                                            <Globe className="w-2.5 h-2.5 text-amber-500" />
                                                                            <span className="text-[7px] font-black text-amber-600 uppercase tracking-widest">Linked Search</span>
                                                                        </div>
                                                                    )}
                                                                    {analysis.is_original && (
                                                                        <span className="px-2 py-0.5 rounded bg-indigo-600 text-white text-[7px] font-black uppercase tracking-widest shadow-lg shadow-indigo-100">
                                                                            Original Creator
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {(() => {
                                                                    const addressParts = getAddressParts(analysis.address);
                                                                    if (addressParts.length <= 1) {
                                                                        return (
                                                                            <p className="text-xs font-bold text-slate-900 truncate pr-2 group-hover:text-indigo-600 transition-colors">
                                                                                {cleanAddress(analysis.address) || `Analysis Record #${analysis.id}`}
                                                                            </p>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <div className="space-y-1">
                                                                            <p className="text-xs font-bold text-slate-900 truncate pr-2 group-hover:text-indigo-600 transition-colors">
                                                                                {addressParts[0]}
                                                                            </p>
                                                                            <div className="pl-3 border-l border-slate-200 space-y-0.5">
                                                                                {addressParts.slice(1).map((addr, aIdx) => (
                                                                                    <div key={aIdx} className="flex items-center gap-1.5 text-[9px] font-bold text-slate-400">
                                                                                        <div className="w-1 h-1 rounded-full bg-slate-300" />
                                                                                        <span className="truncate leading-none">{addr}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                                                {!analysis.is_original && analysis.creator_email && (
                                                                    <div className="group/creator relative">
                                                                        <p className="text-[9px] font-bold text-slate-400 mt-1 flex items-center gap-1.5">
                                                                            <span className="uppercase tracking-widest text-[7px] text-slate-300">Shared From:</span>
                                                                            <span className="text-indigo-400 border-b border-indigo-200/50 border-dotted cursor-help">
                                                                                {analysis.creator_email}
                                                                            </span>
                                                                        </p>
                                                                        {/* Hover Creator Detail */}
                                                                        <div className="absolute left-0 top-full mt-1 opacity-0 group-hover/creator:opacity-100 transition-all z-50 bg-slate-900 text-white p-2.5 rounded-xl shadow-2xl text-[8px] font-black uppercase tracking-widest min-w-[180px] pointer-events-none border border-slate-700">
                                                                            <div className="flex flex-col gap-1">
                                                                                <div className="flex items-center gap-2 text-indigo-300 mb-0.5">
                                                                                    <div className="w-1 h-1 rounded-full bg-indigo-400" />
                                                                                    <span>Original Creator Info</span>
                                                                                </div>
                                                                                <span className="text-white text-[10px] tracking-tight">{analysis.creator_name || 'Anonymous User'}</span>
                                                                                <span className="text-slate-400 lowercase italic font-normal tracking-normal text-[9px]">{analysis.creator_email}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-shrink-0 relative z-10">
                                                                <div className="w-7 h-7 rounded-lg bg-indigo-50/50 flex items-center justify-center text-indigo-300 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                                                </div>
                                                                {analysis.is_original && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setDeletingAnalysisId(analysis.id);
                                                                        }}
                                                                        className="w-7 h-7 rounded-lg bg-rose-50 hover:bg-rose-600 text-rose-500 hover:text-white flex items-center justify-center transition-all border border-rose-100/50"
                                                                        title="Delete Property Analysis"
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="py-20 flex flex-col items-center justify-center text-center opacity-40">
                                            <Activity className="w-6 h-6 text-slate-300 mb-2" />
                                            <p className="text-[9px] font-black uppercase tracking-widest">No Activity Recorded</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-[9px] font-black text-slate-300 uppercase tracking-[0.3em]">Access Telemetry</span>
                                        <span className="text-[9px] font-bold text-slate-400">{activity.logs.length} sessions</span>
                                    </div>

                                    {activity.logs.length > 0 ? (
                                        <div className="space-y-2">
                                            {activity.logs.map((log, idx) => (
                                                <motion.div
                                                    key={log.id}
                                                    initial={{ opacity: 0, scale: 0.98 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    transition={{ delay: idx * 0.02 }}
                                                    className="p-4 bg-slate-50/50 border border-slate-100 rounded-2xl flex items-center justify-between hover:bg-white transition-all group"
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${log.method === 'google' ? 'bg-blue-50 text-blue-500' : 'bg-slate-900 text-white'}`}>
                                                            {log.method === 'google' ? <Globe className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="text-[8px] font-black text-slate-300 uppercase tracking-widest">{formatDate(log.login_time)}</span>
                                                            <p className="text-[10px] font-black text-slate-900 uppercase tracking-tight">{log.method} auth</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-100/50">
                                                        <div className="w-1 h-1 rounded-full bg-emerald-500" />
                                                        <span className="text-[8px] font-black uppercase tracking-widest">OK</span>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="py-20 flex flex-col items-center justify-center text-center opacity-40">
                                            <History className="w-6 h-6 text-slate-300 mb-2" />
                                            <p className="text-[9px] font-black uppercase tracking-widest">No Logs Available</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="p-8 pt-4 border-t border-slate-50">
                            <div className="flex items-center justify-between grayscale opacity-20">
                                <span className="text-[8px] font-black uppercase tracking-[0.4em]">Intelligence Panel</span>
                                <div className="w-1 h-1 rounded-full bg-slate-900" />
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

const UserBase = () => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(7);
    const [updatingUserId, setUpdatingUserId] = useState(null);
    const [activeAction, setActiveAction] = useState({ userId: null, targetRole: null, user: null });
    const [selectedUser, setSelectedUser] = useState(null);
    const [isActivityPanelOpen, setIsActivityPanelOpen] = useState(false);

    const { user: currentUser } = useSelector((state) => state.user);
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [isDeletingUser, setIsDeletingUser] = useState(false);

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE}/admin/users`, {
                headers: { 'Authorization': `Bearer ` + token }
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const filteredUsers = useMemo(() => {
        const filtered = users.filter(user =>
            (user.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Sort by ID descending to ensure latest registered users appear first
        return [...filtered].sort((a, b) => (b.id || 0) - (a.id || 0));
    }, [users, searchTerm]);

    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleRoleUpdate = (user, newRole) => {
        setActiveAction({ userId: user.id, targetRole: newRole, user });
    };

    const confirmRoleUpdate = async () => {
        const { user, targetRole } = activeAction;
        if (!user || !targetRole) return;

        setUpdatingUserId(user.id);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE}/admin/users/${user.id}/role`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ` + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ role: targetRole })
            });

            if (response.ok) {
                setUsers(prev => prev.map(u =>
                    u.id === user.id ? { ...u, access_role: targetRole } : u
                ));
                toast.success(`User role successfully updated to ${targetRole}`, {
                    icon: <ShieldCheck className="w-5 h-5 text-indigo-600" />,
                    className: 'rounded-2xl font-bold text-xs uppercase tracking-wider shadow-2xl border-l-4 border-indigo-600'
                });
                setActiveAction({ userId: null, targetRole: null, user: null });
            } else {
                const err = await response.json();
                toast.error(err.detail || "Failed to update role");
            }
        } catch (error) {
            console.error("Role update failed:", error);
            toast.error("Network error occurred.");
        } finally {
            setUpdatingUserId(null);
        }
    };

    const handleDeleteUser = async (user) => {
        setIsDeletingUser(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE}/admin/users/${user.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ` + token }
            });

            if (response.ok) {
                setUsers(prev => prev.filter(u => u.id !== user.id));
                toast.success(`User account and all associated data successfully deleted`, {
                    icon: <ShieldCheck className="w-5 h-5 text-rose-600" />,
                    className: 'rounded-2xl font-bold text-xs uppercase tracking-wider shadow-2xl border-l-4 border-rose-600'
                });
                setDeletingUserId(null);
            } else {
                const err = await response.json();
                toast.error(err.detail || "Failed to delete user");
            }
        } catch (error) {
            console.error("User deletion failed:", error);
            toast.error("Network error occurred.");
        } finally {
            setIsDeletingUser(false);
        }
    };

    const handleUserClick = (user) => {
        setSelectedUser(user);
        setIsActivityPanelOpen(true);
    };

    return (
        <div className="flex flex-col h-full space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Action Bar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                    <div className="flex flex-col gap-2 pl-4">
                        <div className="flex items-center space-x-3 mb-1">
                            <div className="w-10 h-1 bg-indigo-600 rounded-full"></div>
                            <h1 className="text-5xl font-black text-slate-900 tracking-tighter leading-none flex items-center gap-4">
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-500">User <span className="text-indigo-600">Base</span></span>
                                <div className="h-2 w-2 rounded-full bg-indigo-500 mt-2 animate-pulse" />
                            </h1>
                        </div>
                        <p className="text-slate-500 font-bold text-sm mt-1 max-w-xl leading-relaxed">
                            {filteredUsers.length} Total Registered Members across the platform
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="relative group">
                        <SearchIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-600 transition-colors" />
                        <input
                            type="text"
                            placeholder="Search signatures..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="pl-12 pr-6 py-3 bg-white rounded-2xl text-xs font-bold border border-slate-200/60 focus:border-indigo-600/30 w-72 transition-all outline-none shadow-sm focus:shadow-md"
                        />
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 min-h-0 bg-white rounded-[2.5rem] border border-slate-200/60 shadow-2xl shadow-slate-200/40 flex flex-col overflow-hidden relative">
                <div className="overflow-x-auto flex-1 no-scrollbar relative">
                    <table className="w-full text-left border-collapse min-w-[1000px]">
                        <thead className="sticky top-0 z-40">
                            <tr className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border-b border-white/10">
                                <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">Member Profile</th>
                                <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">Subscription Status</th>
                                <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">
                                    <div className="flex items-center gap-2">
                                        <span>Properties Checked</span>
                                        <Tooltip content="Tracks original property searches vs. properties viewed via shared links or global index.">
                                            <Info className="w-3.5 h-3.5 text-indigo-400 cursor-help" />
                                        </Tooltip>
                                    </div>
                                </th>
                                <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em] text-right">Access Role</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/50">
                            {isLoading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="4" className="px-10 py-8">
                                            <div className="h-12 bg-slate-50 rounded-2xl w-full"></div>
                                        </td>
                                    </tr>
                                ))
                            ) : paginatedUsers.length > 0 ? (
                                paginatedUsers.map((user, idx) => (
                                    <motion.tr
                                        key={user.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                        onClick={() => handleUserClick(user)}
                                        className="group transition-all cursor-pointer relative"
                                    >
                                        <td className="px-10 py-4 relative z-10">
                                            <div className="flex items-center gap-5">
                                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-50 to-indigo-50 border border-slate-200/50 flex items-center justify-center text-slate-400 group-hover:scale-115 group-hover:bg-slate-900 group-hover:text-white transition-all duration-500 shadow-sm overflow-hidden relative">
                                                    <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                                    {user.profile_pic ? (
                                                        <img src={user.profile_pic} alt={user.full_name || 'User'} className="w-full h-full object-cover relative z-10" referrerPolicy="no-referrer" />
                                                    ) : (
                                                        <span className="font-black text-sm relative z-10">{(user.full_name || user.email).charAt(0).toUpperCase()}</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-xs font-black text-slate-900 leading-none tracking-tight group-hover:text-indigo-600 transition-colors uppercase">{user.full_name || 'Anonymous Entity'}</p>
                                                        {user.is_verified && <ShieldCheck className="w-3 h-3 text-emerald-500" />}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-2">
                                                        <Mail className="w-3 h-3 text-slate-300" />
                                                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.1em]">{user.email}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-10 py-4 relative z-10">
                                            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${getPlanDetails(user.plan_name, user.subscription_status).bg}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${getPlanDetails(user.plan_name, user.subscription_status).dot} ${user.subscription_status === 'active' ? 'animate-pulse' : ''}`} />
                                                {getPlanDetails(user.plan_name, user.subscription_status).label}
                                            </div>
                                        </td>
                                        <td className="px-10 py-4 relative z-10">
                                            <div className="flex items-center gap-8">
                                                <div className="flex flex-col">
                                                    <span className="text-xl font-black text-slate-900 tracking-tighter tabular-nums">{user.analyses_count || 0}</span>
                                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Total Checks</span>
                                                </div>
                                                <div className="flex flex-col gap-1.5 border-l border-slate-100 pl-8">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 shadow-[0_0_8px_rgba(79,70,229,0.4)]" />
                                                        <Tooltip content="Properties where this user was the first original creator.">
                                                            <span className="text-[10px] font-black text-slate-700 w-4">{user.original_count || 0}</span>
                                                        </Tooltip>
                                                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest text-slate-400/60">Original</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.4)]" />
                                                        <Tooltip content="Existing properties viewed via global search or shared access.">
                                                            <span className="text-[10px] font-black text-slate-700 w-4">{user.associated_count || 0}</span>
                                                        </Tooltip>
                                                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest text-slate-400/60">Linked</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-10 py-4 text-right relative z-30">
                                            <div className="flex items-center justify-end gap-3 min-h-[40px]">
                                                <AnimatePresence mode="wait">
                                                    {deletingUserId === user.id ? (
                                                        <InlineDeleteUserOverlay
                                                            key="delete-overlay"
                                                            user={user}
                                                            onConfirm={() => handleDeleteUser(user)}
                                                            onCancel={() => setDeletingUserId(null)}
                                                            isDeleting={isDeletingUser}
                                                        />
                                                    ) : activeAction.userId === user.id ? (
                                                        <InlineActionOverlay
                                                            key="overlay"
                                                            user={user}
                                                            targetRole={activeAction.targetRole}
                                                            onConfirm={confirmRoleUpdate}
                                                            onCancel={() => setActiveAction({ userId: null, targetRole: null, user: null })}
                                                            isUpdating={updatingUserId === user.id}
                                                        />
                                                    ) : (
                                                        <motion.div
                                                            key="badge"
                                                            initial={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            exit={{ opacity: 0 }}
                                                            className="flex items-center gap-3"
                                                        >
                                                            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${user.access_role === 'admin'
                                                                ? 'bg-indigo-600 text-white border-indigo-500 shadow-xl shadow-indigo-100'
                                                                : user.subscription_status === 'active'
                                                                    ? 'bg-slate-900 text-white border-slate-800 shadow-xl shadow-slate-100'
                                                                    : 'bg-white text-slate-400 border-slate-100'
                                                                }`}>
                                                                {user.access_role === 'admin' ? <ShieldCheck className="w-3 h-3 text-indigo-200" /> : <Zap className={`w-3 h-3 ${user.subscription_status === 'active' ? 'text-indigo-400' : 'text-slate-300'}`} />}
                                                                {user.access_role || 'users'}
                                                            </div>

                                                            {currentUser?.id !== user.id && (
                                                                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setDeletingUserId(null);
                                                                            handleRoleUpdate(user, user.access_role === 'admin' ? 'users' : 'admin');
                                                                        }}
                                                                        disabled={updatingUserId === user.id}
                                                                        className={`p-2 rounded-lg border border-slate-200 hover:bg-slate-900 hover:text-white transition-all group/btn flex items-center justify-center ${updatingUserId === user.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                        title={user.access_role === 'admin' ? 'Demote to User' : 'Promote to Admin'}
                                                                    >
                                                                        <UserCog className="w-3.5 h-3.5 text-slate-400 group-hover/btn:text-white" />
                                                                    </button>

                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setActiveAction({ userId: null, targetRole: null, user: null });
                                                                            setDeletingUserId(user.id);
                                                                        }}
                                                                        disabled={isDeletingUser}
                                                                        className="p-2 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-600 hover:text-white text-rose-500 transition-all flex items-center justify-center"
                                                                        title="Delete User Account"
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </td>
                                        <td className="absolute inset-x-4 inset-y-2 bg-slate-50/80 backdrop-blur-md rounded-3xl border border-slate-200/40 opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none z-0 scale-95 group-hover:scale-100 shadow-sm" colSpan="4" />
                                    </motion.tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="px-10 py-32 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-4">
                                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center">
                                                <SearchIcon className="w-6 h-6 text-slate-200" />
                                            </div>
                                            <p className="text-[11px] font-black text-slate-300 uppercase tracking-[0.4em]">Zero Encounters in Current Sector</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* High-Standard Pagination Controller */}
                <div className="px-10 py-6 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <div className="flex items-center gap-6">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            Showing <span className="text-slate-900">{Math.min(filteredUsers.length, (currentPage - 1) * itemsPerPage + 1)}-{Math.min(filteredUsers.length, currentPage * itemsPerPage)}</span> of <span className="text-slate-900">{filteredUsers.length}</span> results
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-2 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-slate-900 hover:shadow-md disabled:opacity-30 disabled:hover:shadow-none transition-all"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>

                        <div className="flex items-center px-2 gap-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const pageNum = totalPages > 5 && currentPage > 3
                                    ? currentPage - 2 + i
                                    : i + 1;

                                if (pageNum > totalPages) return null;

                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`w-8 h-8 rounded-xl text-[10px] font-black transition-all ${currentPage === pageNum
                                            ? 'bg-slate-900 text-white shadow-lg shadow-slate-200'
                                            : 'text-slate-400 hover:bg-white hover:text-slate-900'
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-2 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-slate-900 hover:shadow-md disabled:opacity-30 disabled:hover:shadow-none transition-all"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Activity Side Panel */}
            <UserActivityPanel
                user={selectedUser}
                isOpen={isActivityPanelOpen}
                onClose={() => setIsActivityPanelOpen(false)}
                onAnalysisDeleted={fetchUsers}
            />
        </div>
    );
};

export default UserBase;
